import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import usePromptCollection from "@/hooks/usePromptCollection";
import useReminderMutation from "@/hooks/useReminderMutation";
import { QUERY_KEYS } from "@/libraries/constant";
import { isAxiosError } from "@/libraries/helper";
import { getReminder } from "@/queries";
import NotFound from "@/routes/-components/NotFound";
import { DataIdSchema, ReminderFormFieldSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import IconButton from "@mui/material/IconButton";
import { onlineManager, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import RemminderForm from "../-components/RemminderForm";

export const Route = createFileRoute("/dashboard/inbox/reminders/$id")({
	params: {
		parse: (params) => ({
			id: DataIdSchema.parse(Number(params.id)),
		}),
		stringify: ({ id }) => ({ id: id.toString() }),
	},
	loader: async ({ context, params }) => {
		try {
			if (onlineManager.isOnline() && !context.isRestoring) {
				const reminder = await context.queryClient.ensureQueryData(
					getReminder(params.id),
				);
				return reminder;
			}
			return context.queryClient.getQueryData([
				QUERY_KEYS.GET_REMINDER,
				params.id,
			]);
		} catch (error) {
			if (isAxiosError(error, 404)) throw notFound();
		}
	},
	component: RouteComponent,
	notFoundComponent: NotFound,
});

function RouteComponent() {
	const reminderId = Route.useParams({
		select: (s) => s.id,
	});
	const onBack = useBackward();
	const reminderQuery = useSuspenseQuery(getReminder(reminderId));
	const reminder = reminderQuery.data?.reminder;
	const form = useForm<ReminderFormFieldSchema>({
		mode: "all",
		resolver: zodResolver(ReminderFormFieldSchema),
		// @ts-expect-error
		defaultValues: async () => Promise.resolve(reminder || { repeat: "Never" }),
	});

	const { deleteTodo } = usePromptCollection();
	const { remove } = useReminderMutation();

	const onDeleteTodo = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			return remove.mutate(reminderId, {
				onSuccess: () => {
					toast.success("To-do item deleted");
					return onBack();
				},
				onError: (err) => {
					if (isAxiosError(err)) {
						return toast.error((err.response?.data as any)?.messages);
					}
					return toast.error("To-do item failed to deleted");
				},
			});
		},
		[onBack, remove, reminderId],
	);

	return (
		<>
			<BackAppBar
				className="!bg-gradient-contact [&_*_]:text-white"
				title="Reminder Details"
				titleClassName="flex-1"
				rightContent={
					<IconButton edge="end" onClick={() => deleteTodo(onDeleteTodo)}>
						<DeleteRoundedIcon />
					</IconButton>
				}
			/>
			<FormProvider {...form}>
				<div className="container py-3 h-full">
					<RemminderForm />
				</div>
			</FormProvider>
		</>
	);
}
